import ErrorBoundary from '@components/ErrorBoundary';
import Spinner from '@components/spinner/Spinner';
import { useUpdateCategory } from '@hooks/postQueries';
import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import formatString from '@utils/formatString';
import { Suspense, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

function CategoryUpdateModal({
  openCategoryModal,
  closeCategoryModal,
  interviewCodebook,
  setOpenCodeModal,
  setSelectedCode,
  selectedNode,
  id,
  handleClosePopover,
  getColorByCategory,
  fetchThemesAndCategories,
  refetch,
}) {
  let closeAllModalAfterSuccess = () => {
    fetchThemesAndCategories();
    refetch();
    closeCategoryModal();
    handleClosePopover();
  };
  const [isSummaryExpanded, setisSummaryExpanded] = useState(false);
  const { mutate: updateCategory, isPending: isUpdatingCategory } =
    useUpdateCategory(closeAllModalAfterSuccess);
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      category: formatString(selectedNode?.name ?? ''),
      description: '',
    },
  });

  useEffect(() => {
    Object.entries(interviewCodebook?.categories || {})
      .filter(
        ([categoryKey]) => formatString(categoryKey) === formatString(selectedNode?.name || ''),
      )
      .map(([_, categoryData]) => {
        setValue('description', categoryData?.description);
      });
    setValue('category', formatString(selectedNode?.name || ''));
  }, [selectedNode]);

  const handleCategoryUpdate = (data) => {
    let payload = {
      study_id: id,
      old_category_name: selectedNode?.name,
      new_category_name: data.category,
      category_description: data.description,
    };
    updateCategory({
      payload,
    });
  };

  return (
    <Dialog open={openCategoryModal} onClose={closeCategoryModal} size="md" maxWidth="800px">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <DialogTitle>
          <Typography variant="h6">Category Details</Typography>
        </DialogTitle>

        <Box display={'flex'} bgcl gap={1.5} justifyContent={'end'} marginRight={'20px'}>
          <Button
            variant="custom"
            sx={{ ml: 'auto', cursor: 'pointer' }}
            onClick={closeCategoryModal}
          >
            Cancel
          </Button>
          <Button
            disabled={isUpdatingCategory}
            sx={{ cursor: 'pointer' }}
            type="submit"
            form="myForm"
            variant="contained"
          >
            {isUpdatingCategory ? 'Saving' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
      <Divider />
      <DialogContent
        sx={{ maxWidth: '900px', minWidth: '700px', position: 'relative', padding: '0' }}
      >
        <ErrorBoundary>
          <Suspense fallback={<Spinner />}>
            {Object.entries(interviewCodebook?.categories || {})
              .filter(
                ([categoryKey]) =>
                  formatString(categoryKey) === formatString(selectedNode?.name || ''),
              )
              .map(([categoryKey, categoryData]) => (
                <form id="myForm" onSubmit={handleSubmit(handleCategoryUpdate)} key={categoryKey}>
                  <Box p={2} key={categoryKey}>
                    <Stack direction="row" alignItems="start" spacing={2} fullWidth>
                      <FormLabel sx={{ minWidth: 150 }}>Category</FormLabel>
                      <Controller
                        name="category"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              '&:hover .edit-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            <TextField {...field} variant="outlined" size="small" fullWidth />
                            <IconButton
                              className="edit-icon"
                              sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                padding: 0,
                              }}
                            >
                              <Icon icon="ri:edit-fill" width="24" color="#7d5ae8" height="24" />
                            </IconButton>
                          </Box>
                        )}
                      />
                    </Stack>
                    <Stack direction="row" mt={1} alignItems="start" spacing={2} fullWidth>
                      <FormLabel sx={{ minWidth: 150 }}>Definition</FormLabel>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              '&:hover .edit-icon': {
                                opacity: 1,
                              },
                            }}
                          >
                            <TextareaAutosize
                              aria-label="minimum height"
                              {...field}
                              minRows={2}
                              placeholder="Minimum 3 rows"
                              style={{
                                width: '100%',
                                border: '1px solid #dfe5ef',
                                padding: '10px',
                                fontFamily: 'inherit',
                                borderRadius: '4px',
                                color: '#333333',
                              }}
                            />
                            <IconButton
                              className="edit-icon"
                              sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                padding: 0,
                              }}
                            >
                              <Icon icon="ri:edit-fill" width="24" color="#7d5ae8" height="24" />
                            </IconButton>
                          </Box>
                        )}
                      />
                    </Stack>

                    {categoryData.summary && (
                      <>
                        <Divider sx={{ mt: 2 }} />
                        <Typography mt={1}>
                          <span style={{ fontWeight: '600' }}>Summary : </span>{' '}
                          <span>
                            {' '}
                            {isSummaryExpanded
                              ? categoryData.summary
                              : `${categoryData.summary?.slice(0, 220)}...`}
                            <span
                              onClick={() => setisSummaryExpanded(!isSummaryExpanded)}
                              onKeyPress={() => setisSummaryExpanded(!isSummaryExpanded)}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isSummaryExpanded}
                              style={{
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                marginLeft: '10px',
                              }}
                            >
                              {isSummaryExpanded ? (
                                <span style={{ color: '#7d5ae8' }}>See Less</span>
                              ) : (
                                <span style={{ color: '#7d5ae8' }}>See More</span>
                              )}
                            </span>
                          </span>
                        </Typography>
                      </>
                    )}
                    <Typography>
                      <span style={{ fontWeight: '600' }}>Saliency : </span>{' '}
                      {categoryData?.saliency}
                    </Typography>

                    <Divider sx={{ mt: 1 }} />

                    {/* Codes Section */}
                    <Typography variant="subtitle2" mt={1}>
                      Codes:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 1, columnGap: 1 }} mt={1}>
                      {categoryData.codes_included.map((code) => (
                        <CodeChip
                          key={code}
                          definition={categoryData.description}
                          saliency={categoryData.saliency}
                          code={code}
                          categoryData={categoryData}
                          categoryKey={categoryKey}
                          setOpenCodeModal={setOpenCodeModal}
                          setSelectedCode={setSelectedCode}
                          getColorByCategory={getColorByCategory}
                        />
                      ))}
                    </Stack>

                    <Divider sx={{ mt: 2 }} />

                    {/* Example Quotes Section */}
                    <Typography variant="subtitle2" mt={1}>
                      Example Quotes:
                    </Typography>
                    <ExampleQuotes quotes={categoryData.example_quotes} />
                  </Box>
                </form>
              ))}
          </Suspense>
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}

export default CategoryUpdateModal;

const CodeChip = ({
  code,
  definition,
  saliency,
  categoryKey,
  setOpenCodeModal,
  setSelectedCode,
  getColorByCategory,
}) => (
  <Chip
    label={formatString(code)}
    sx={{
      backgroundColor: getColorByCategory(categoryKey),
      borderRadius: '4px',
      '& .MuiChip-root': {
        borderRadius: '4px',
      },
    }}
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      setOpenCodeModal(true);
      setSelectedCode({
        name: code,
        definition,
        location: 0,
        saliency,
        categoryKey,
      });
    }}
  />
);

const ExampleQuotes = ({ quotes }) => (
  <Stack spacing={1} mt={1}>
    {quotes?.length ? (
      quotes.map((quote, index) => (
        <Typography fontStyle={'italic'} key={index} variant="body2">
          {quote?.text || ''}
        </Typography>
      ))
    ) : (
      <Typography variant="body2" color="textSecondary">
        No example quotes available.
      </Typography>
    )}
  </Stack>
);
